/**
 * Prev/next arrows + a scroll-position dot navigator for the featured
 * product/collection strip inside the mobile menu drawer
 * (snippets/header-drawer.liquid, .tng-fc-nav / [data-tng-fc-dots]).
 * The strip itself is a plain scrollable <ul> — this adds button-driven
 * scrolling with wraparound (next past the last item loops to the first,
 * prev before the first loops to the last) and dots that track native
 * swipe/scroll too.
 *
 * Uses event delegation on document instead of binding to the buttons
 * directly: Shopify's header-drawer/header-menu custom elements re-render
 * their subtree after first paint, which would detach listeners bound to
 * the original (soon-replaced) button/list nodes. Scroll events don't
 * bubble, so the dot-sync listener is registered with `capture: true`
 * instead (same trick assets/dialog.js uses for the native `toggle`
 * event) -- that still reaches a freshly re-rendered list without
 * re-binding anything.
 */
(function () {
  'use strict';

  function scrollStep(list) {
    var item = list.querySelector('.menu-drawer__featured-content-list-item');
    if (!item) return list.clientWidth * 0.8;
    var style = getComputedStyle(list);
    var gap = parseFloat(style.columnGap || style.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  function atStart(list) {
    return list.scrollLeft <= 2;
  }

  function atEnd(list) {
    return list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  }

  function goNext(list) {
    if (atEnd(list)) {
      list.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      list.scrollBy({ left: scrollStep(list), behavior: 'smooth' });
    }
  }

  function goPrev(list) {
    if (atStart(list)) {
      list.scrollTo({ left: list.scrollWidth, behavior: 'smooth' });
    } else {
      list.scrollBy({ left: -scrollStep(list), behavior: 'smooth' });
    }
  }

  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest('[data-tng-fc-prev], [data-tng-fc-next]');
    if (!btn) return;
    var nav = btn.closest('.tng-fc-nav');
    var list = nav && nav.querySelector('[data-tng-fc-list]');
    if (!list) return;
    ev.preventDefault();
    if (btn.hasAttribute('data-tng-fc-next')) goNext(list);
    else goPrev(list);
    syncDots(list);
  });

  function findDots(list) {
    var nav = list.closest('.tng-fc-nav');
    var dots = nav && nav.nextElementSibling;
    return dots && dots.hasAttribute('data-tng-fc-dots') ? dots : null;
  }

  function buildDots(list, dots) {
    var count = list.querySelectorAll('.menu-drawer__featured-content-list-item').length;
    if (String(count) === dots.dataset.tngFcBuilt) return;
    dots.dataset.tngFcBuilt = String(count);
    dots.textContent = '';
    if (count <= 1) return;
    for (var i = 0; i < count; i++) {
      var d = document.createElement('span');
      d.className = 'tng-fc-dot' + (i === 0 ? ' is-active' : '');
      dots.appendChild(d);
    }
  }

  function syncDots(list) {
    var dots = findDots(list);
    if (!dots) return;
    buildDots(list, dots);
    var step = scrollStep(list);
    var idx = step ? Math.round(list.scrollLeft / step) : 0;
    var items = dots.querySelectorAll('.tng-fc-dot');
    idx = Math.max(0, Math.min(idx, items.length - 1));
    items.forEach(function (d, i) {
      d.classList.toggle('is-active', i === idx);
    });
  }

  document.addEventListener(
    'scroll',
    function (ev) {
      var list = ev.target;
      if (!list || !list.hasAttribute || !list.hasAttribute('data-tng-fc-list')) return;
      syncDots(list);
    },
    true
  );

  // Build dots for lists already on the page (initial load, and after
  // Shopify's Section Rendering API replaces the drawer's subtree).
  new MutationObserver(function () {
    document.querySelectorAll('[data-tng-fc-list]').forEach(syncDots);
  }).observe(document.body, { childList: true, subtree: true });
})();
